import { useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ArrowLeft, Download, FileText, MapPin } from "lucide-react";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" }) : "");

export default function ArticleDetail() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const isPreview = params.get("preview") === "local";
  const [post, setPost] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (isPreview) {
      try { const p = JSON.parse(localStorage.getItem("dcep-article-preview") || ""); setPost(p); } catch { setNotFound(true); }
    } else {
      api.get(`/posts/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true));
    }
    api.get("/markers").then((r) => setMarkers(r.data)).catch(() => {});
  }, [slug, isPreview]);

  if (notFound) return <Layout><div className="max-w-2xl mx-auto px-5 py-32 text-center"><h1 className="font-serif text-3xl text-[#11181C]">Article not found</h1><Link to="/research" className="mt-5 inline-block text-[#059669] font-medium">← Back to research</Link></div></Layout>;
  if (!post) return <Layout><div className="py-32 text-center text-[#52606D]">Loading…</div></Layout>;

  const images = (post.attachments || []).filter((a) => a.kind === "image");
  const videos = (post.attachments || []).filter((a) => a.kind === "video");
  const files = (post.attachments || []).filter((a) => a.kind === "file");
  const linkedMarkers = (post.linked_marker_ids || []).map((id) => markers.find((m) => m.id === id)).filter(Boolean);

  return (
    <Layout>
      {isPreview && (
        <div className="bg-amber-50 border-b border-amber-200 text-amber-900 text-sm" data-testid="article-preview-banner">
          <div className="max-w-3xl mx-auto px-5 lg:px-8 py-3 flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="font-semibold">Preview</span>
            <span>This is how the article will look once published. It is not live and nobody else can see this page.</span>
            {post.status === "draft" && <span className="font-mono text-[10px] uppercase px-2 py-0.5 rounded-full bg-amber-200 text-amber-900">Draft</span>}
          </div>
        </div>
      )}
      <article className="max-w-3xl mx-auto px-5 lg:px-8 py-12 lg:py-16">
        <Link to="/research" className="inline-flex items-center gap-2 text-sm text-[#52606D] hover:text-[#11181C] mb-8" data-testid="article-back">
          <ArrowLeft size={15} /> Back to research
        </Link>
        <div className="flex items-center gap-3 font-mono text-[11px] uppercase tracking-[0.14em] text-[#52606D] mb-5">
          <span className="text-[#059669]">{post.category}</span><span>·</span><span>{fmtDate(post.published_at)}</span><span>·</span><span>{post.read_minutes || 5} min read</span>
        </div>
        <h1 className="font-serif text-4xl lg:text-5xl leading-[1.08] text-[#11181C] tracking-tight">{post.title}</h1>
        {post.excerpt && <p className="mt-6 text-xl text-[#52606D] leading-relaxed font-serif italic">{post.excerpt}</p>}
        <div className="mt-6 flex items-center gap-3 text-sm text-[#52606D] border-y border-slate-200 py-4">
          <span>By {post.author || "DCEPtNZ"}</span>
          {post.region && <><span>·</span><span>{post.region}</span></>}
          {post.infrastructure_type && <><span>·</span><span>{post.infrastructure_type}</span></>}
        </div>

        {post.cover_image_url && (
          <img src={fileUrl(post.cover_image_url)} alt={post.title} className="w-full rounded-xl mt-8 border border-slate-200" />
        )}

        <div className="prose-dcep mt-10 text-[#1b2933] leading-[1.8] text-[17px] space-y-5
          [&_h2]:font-serif [&_h2]:text-2xl [&_h2]:text-[#11181C] [&_h2]:mt-10 [&_h2]:mb-3
          [&_h3]:font-serif [&_h3]:text-xl [&_h3]:text-[#11181C] [&_h3]:mt-8 [&_h3]:mb-2
          [&_a]:text-[#2E8BFF] [&_a]:underline
          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-1.5
          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:space-y-1.5
          [&_blockquote]:border-l-2 [&_blockquote]:border-[#059669] [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:text-[#52606D]
          [&_img]:rounded-lg [&_img]:my-6 [&_code]:font-mono [&_code]:text-sm">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{post.body || ""}</ReactMarkdown>
        </div>

        {/* Media gallery */}
        {images.length > 0 && (
          <div className="mt-12">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#059669] mb-4">Photos</div>
            <div className="grid grid-cols-2 gap-3">
              {images.map((a) => <img key={a.id} src={fileUrl(a.url)} alt={a.original_filename} className="rounded-lg border border-slate-200 w-full" />)}
            </div>
          </div>
        )}
        {videos.length > 0 && (
          <div className="mt-12 space-y-4">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#059669] mb-4">Video</div>
            {videos.map((a) => <video key={a.id} src={fileUrl(a.url)} controls className="w-full rounded-lg border border-slate-200" />)}
          </div>
        )}
        {files.length > 0 && (
          <div className="mt-12">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#059669] mb-4">Documents & downloads</div>
            <div className="space-y-2">
              {files.map((a) => (
                <a key={a.id} href={fileUrl(a.url)} target="_blank" rel="noreferrer" className="flex items-center gap-3 p-3.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-100 transition-colors" data-testid={`article-file-${a.id}`}>
                  <FileText size={18} className="text-[#52606D]" />
                  <span className="text-sm text-[#11181C] flex-1 truncate">{a.original_filename}</span>
                  <Download size={16} className="text-[#059669]" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Linked map sites */}
        {linkedMarkers.length > 0 && (
          <div className="mt-12 rounded-xl border border-slate-200 bg-slate-100 p-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.15em] text-[#059669] mb-4 flex items-center gap-2"><MapPin size={13} /> Related map sites</div>
            <div className="space-y-2">
              {linkedMarkers.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-4 bg-white border border-slate-200 rounded-lg px-4 py-3" data-testid={`article-site-${m.id}`}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-[#11181C] truncate">{m.name}</div>
                    <div className="text-xs text-[#52606D]">{[m.operator, m.region].filter(Boolean).join(" · ")}</div>
                  </div>
                  <Link to={`/map?site=${m.id}`} className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[#0B0F17] text-white text-xs font-medium hover:bg-[#1b2230] transition-colors" data-testid={`article-view-on-map-${m.id}`}>
                    <MapPin size={13} /> View on map
                  </Link>
                </div>
              ))}
            </div>
          </div>
        )}
      </article>
    </Layout>
  );
}
